import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Paper,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
} from '@mui/material';
import { Save, ArrowBack, Cancel } from '@mui/icons-material';
import useAxiosPrivate from '../../../Hooks/useAxiosPrivate';
import styles from './AddReport.module.css';

const AddReport = () => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    reportName: '',
    reportDescription: '',
    reportType: '',
    apiEndpoint: '',
    requiresEvent: false,
    sortOrder: 0,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.reportName.trim()) {
      newErrors.reportName = 'Report name is required';
    }
    
    if (!formData.reportType.trim()) {
      newErrors.reportType = 'Report type is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.reportType)) {
      newErrors.reportType = 'Report type must be lowercase with hyphens (e.g., my-report)';
    }
    
    if (!formData.apiEndpoint.trim()) {
      newErrors.apiEndpoint = 'API endpoint is required';
    } else if (!formData.apiEndpoint.startsWith('/api/reports/')) {
      newErrors.apiEndpoint = 'API endpoint must start with /api/reports/';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      enqueueSnackbar('Please fix validation errors', { variant: 'warning' });
      return;
    }

    setIsSaving(true);
    
    try {
      await axiosPrivate.post('/api/system-reports', formData);
      enqueueSnackbar('Report added successfully!', { variant: 'success' });
      navigate('/admin/reporting');
    } catch (error) {
      console.error('Error adding report:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add report';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main>
      {/* Blue header section */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Add New Report</h1>
              <p>Create a new report definition that admins can run and export.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content section */}
      <div className="full-row white">
        <div className="container-fluid">
          <div className="row mt-4">
            <div className="col-md-8 offset-md-2">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/admin/reporting')}
              >
                Back to Reports
              </Button>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-md-8 offset-md-2">
              <Paper elevation={2} className={styles.formPanel}>
                <Box p={4}>
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Report Name"
                      name="reportName"
                      value={formData.reportName}
                      onChange={handleChange}
                      margin="normal"
                      required
                      error={Boolean(errors.reportName)}
                      helperText={errors.reportName || 'Display name shown to users (e.g., "Project Roster")'}
                    />

                    <TextField
                      fullWidth
                      label="Report Description"
                      name="reportDescription"
                      value={formData.reportDescription}
                      onChange={handleChange}
                      margin="normal"
                      multiline
                      rows={3}
                      helperText="Detailed description of what this report generates"
                    />

                    <TextField
                      fullWidth
                      label="Report Type (URL slug)"
                      name="reportType"
                      value={formData.reportType}
                      onChange={handleChange}
                      margin="normal"
                      required
                      error={Boolean(errors.reportType)}
                      helperText={errors.reportType || 'Unique identifier, lowercase with hyphens (e.g., "project-roster")'}
                    />

                    <TextField
                      fullWidth
                      label="API Endpoint"
                      name="apiEndpoint"
                      value={formData.apiEndpoint}
                      onChange={handleChange}
                      margin="normal"
                      required
                      error={Boolean(errors.apiEndpoint)}
                      helperText={errors.apiEndpoint || 'Backend API path (e.g., "/api/reports/project-roster")'}
                    />

                    <TextField
                      fullWidth
                      label="Sort Order"
                      name="sortOrder"
                      type="number"
                      value={formData.sortOrder}
                      onChange={handleChange}
                      margin="normal"
                      helperText="Display order in the reports list (lower numbers appear first)"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.requiresEvent}
                          onChange={handleChange}
                          name="requiresEvent"
                          color="primary"
                        />
                      }
                      label="Requires Event Selection"
                      className={styles.checkbox}
                    />
                    <p className={styles.checkboxHelp}>
                      Check this if the report needs an event to be selected before running
                    </p>

                    <div className={styles.buttonGroup}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        disabled={isSaving}
                        className={styles.saveButton}
                      >
                        {isSaving ? 'Saving...' : 'Save Report'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/admin/reporting')}
                        startIcon={<Cancel />}
                        disabled={isSaving}
                        sx={{ py: '8px', px: '20px' }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Box>
              </Paper>

              {/* Instructions */}
              <Paper elevation={1} className={styles.instructionsPanel}>
                <Box p={3}>
                  <h4>Important Notes</h4>
                  <ul>
                    <li>
                      <strong>Report Type:</strong> Must match the backend implementation. Use lowercase letters and hyphens only.
                    </li>
                    <li>
                      <strong>API Endpoint:</strong> Must be implemented in <code>api/src/routes/reportRoutes.js</code>
                    </li>
                    <li>
                      <strong>Requires Event:</strong> If checked, users will need to select an event before downloading the report.
                    </li>
                    <li>
                      <strong>Backend Implementation:</strong> After adding the report here, you'll need to implement the actual report generation logic in the backend API.
                    </li>
                  </ul>
                </Box>
              </Paper>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AddReport;
